/* L'armoire de Yasmina: announcement bar (sections/ldy-announcement.liquid).
   - Keeps the free-shipping message live from the 'ldy:cart' event broadcast by ldy-commerce.js
     (no extra cart request on page load: the first state is server-rendered).
   - Converts the amount when the guest shops in another currency.
   - Rotates messages gently; pauses on hover/focus and with the pause button. Off with reduced motion. */
(() => {
  if (customElements.get('ldy-announcement')) return;

  const AMOUNT = /\[amount\]|\{\{\s*amount\s*\}\}/;
  const LIVE_CLEAR_MS = 6000;
  const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const trimZeros = (money) => money.replace(/[.,]00(?=\D*$)/, '');

  class LdyAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => !slide.hidden),
      );
      this.ship = this.querySelector('[data-ship]');
      this.live = this.querySelector('[data-live]');
      this.pauseButton = this.querySelector('[data-pause]');
      this.delay = Number(this.dataset.rotate || 0) * 1000;
      this.held = false;
      this.paused = false;

      if (this.ship) {
        this.onCart = (event) => this.renderShip(event.detail, true);
        document.addEventListener('ldy:cart', this.onCart);
        const rate = Number(window.Shopify?.currency?.rate || 1);
        if (rate !== 1) this.renderShip({ total_price: Number(this.ship.dataset.total) || 0 }, false);
      }

      if (this.slides.length > 1 && this.delay > 0 && !prefersReduced()) this.startRotation();
    }

    disconnectedCallback() {
      if (this.onCart) document.removeEventListener('ldy:cart', this.onCart);
      clearTimeout(this.timer);
      clearTimeout(this.liveTimer);
    }

    /* ---------- free shipping ---------- */
    renderShip(cart, announce) {
      const commerce = window.ldyCommerce;
      if (!commerce || !cart) return;
      const fs = commerce.freeShipping(cart);
      if (!fs.enabled) return;

      const state = fs.total <= 0 ? 'empty' : fs.left > 0 ? 'progress' : 'done';
      const amount = state === 'empty' ? trimZeros(commerce.formatMoney(fs.goal)) : commerce.formatMoney(fs.left);
      const template = this.ship.dataset[`tpl${state[0].toUpperCase()}${state.slice(1)}`] || '';
      const text = this.ship.querySelector('[data-ship-text]');
      const parts = template.split(AMOUNT);
      text.replaceChildren(
        ...parts.flatMap((part, i) =>
          i === 0 ? [part] : [Object.assign(document.createElement('b'), { textContent: amount }), part],
        ),
      );

      const meter = this.ship.querySelector('.ldy-meter');
      if (meter) {
        meter.hidden = state !== 'progress';
        meter.firstElementChild?.style.setProperty('--p', String(fs.progress));
      }

      if (!announce) return;
      this.show(this.slides.indexOf(this.ship));
      this.schedule();
      if (this.live) {
        this.live.textContent = text.textContent;
        clearTimeout(this.liveTimer);
        this.liveTimer = setTimeout(() => (this.live.textContent = ''), LIVE_CLEAR_MS);
      }
    }

    /* ---------- rotation ---------- */
    startRotation() {
      const hold = (on) => () => {
        this.held = on;
        this.schedule();
      };
      this.addEventListener('mouseenter', hold(true));
      this.addEventListener('mouseleave', hold(false));
      this.addEventListener('focusin', hold(true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) hold(false)();
      });

      if (this.pauseButton) {
        this.pauseButton.hidden = false;
        this.pauseButton.addEventListener('click', () => {
          this.paused = !this.paused;
          this.pauseButton.setAttribute('aria-pressed', String(this.paused));
          this.schedule();
        });
      }
      this.rotating = true;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.rotating || this.held || this.paused) return;
      this.timer = setTimeout(() => {
        this.show((this.index + 1) % this.slides.length);
        this.schedule();
      }, this.delay);
    }

    show(next) {
      if (next < 0 || next === this.index) return;
      const current = this.slides[this.index];
      const target = this.slides[next];
      if (!target) return;
      if (current) current.hidden = true;
      target.classList.remove('is-entering');
      target.hidden = false;
      void target.offsetWidth;
      target.classList.add('is-entering');
      this.index = next;
    }
  }

  customElements.define('ldy-announcement', LdyAnnouncement);
})();
